import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { workOrdersAPI } from '../../api';
import { PageHeader, Card, CardHeader, CardBody, Button, statusBadge, Modal, Select, Input, Textarea } from '../ui';
import toast from 'react-hot-toast';

export default function WorkOrderDetail() {
  const { id }   = useParams();
  const navigate = useNavigate();
  const [wo, setWo]           = useState(null);
  const [loading, setLoading] = useState(true);
  const [modal, setModal]     = useState(false);
  const [statusForm, setStatusForm] = useState({ status:'', actual_cost:'', notes:'' });
  const [updating, setUpdating] = useState(false);

  const load = () => workOrdersAPI.get(id).then(r => setWo(r.data)).finally(() => setLoading(false));
  useEffect(() => { load(); }, [id]);

  const updateStatus = async () => {
    setUpdating(true);
    try {
      await workOrdersAPI.updateStatus(id, statusForm);
      toast.success('Status updated');
      setModal(false);
      load();
    } catch { toast.error('Failed'); }
    finally { setUpdating(false); }
  };

  if (loading) return <div style={{ padding:40, textAlign:'center', color:'var(--ink-muted)' }}>Loading…</div>;
  if (!wo) return null;

  const specs = [
    ['WO Number', wo.wo_number], ['Asset', wo.asset_code], ['Asset Name', wo.asset_name],
    ['Location', wo.location_name], ['Type', wo.type], ['Priority', wo.priority],
    ['Assigned To', wo.assigned_name||'Unassigned'], ['Assigned By', wo.assigned_by_name],
    ['Estimated Cost', wo.estimated_cost ? `₹${Number(wo.estimated_cost).toLocaleString('en-IN')}` : '—'],
    ['Actual Cost', wo.actual_cost ? `₹${Number(wo.actual_cost).toLocaleString('en-IN')}` : '—'],
    ['Scheduled Date', wo.scheduled_date?.slice(0,10)||'—'],
    ['Due Date', wo.due_date?.slice(0,10)||'—'],
    ['Completed', wo.completed_date?.slice(0,16)||'—'],
    ['Created', wo.created_at?.slice(0,10)],
  ];

  return (
    <div>
      <PageHeader
        title={wo.wo_number}
        subtitle={wo.title}
        actions={
          <div style={{ display:'flex', gap:8 }}>
            {statusBadge.wo(wo.status)}
            <Button onClick={() => { setStatusForm({ status:wo.status, actual_cost:'', notes:'' }); setModal(true); }}>Update Status</Button>
            <Button onClick={() => navigate(`/work-orders/${id}/edit`)}>✏ Edit</Button>
            <Button onClick={() => navigate('/work-orders')}>← Back</Button>
          </div>
        }
      />

      <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr', gap:16 }}>
        <Card>
          <CardHeader title="Work Order Details" actions={statusBadge.priority(wo.priority)} />
          <CardBody>
            {specs.map(([label, val]) => (
              <div key={label} style={{ display:'flex', justifyContent:'space-between', padding:'9px 0', borderBottom:'1px solid var(--border)', fontSize:13 }}>
                <span style={{ color:'var(--ink-muted)' }}>{label}</span>
                <span style={{ fontWeight:500, color:'var(--ink)' }}>{val}</span>
              </div>
            ))}
            {wo.description && (
              <div style={{ marginTop:16, padding:14, background:'var(--surface)', borderRadius:'var(--radius-sm)', fontSize:13, color:'var(--ink-soft)', lineHeight:1.7 }}>
                <div style={{ fontSize:11, fontWeight:700, textTransform:'uppercase', letterSpacing:'.06em', color:'var(--ink-muted)', marginBottom:8 }}>Description</div>
                {wo.description}
              </div>
            )}
            {wo.notes && (
              <div style={{ marginTop:12, padding:14, background:'var(--amber-light)', borderRadius:'var(--radius-sm)', fontSize:13, color:'var(--amber)', lineHeight:1.7 }}>
                <strong>Notes:</strong> {wo.notes}
              </div>
            )}
          </CardBody>
        </Card>

        <Card>
          <CardHeader title="Quick Actions" />
          <CardBody>
            <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
              {[['open','Open'],['assigned','Assigned'],['in_progress','In Progress'],['on_hold','On Hold'],['closed','Closed'],['cancelled','Cancelled']].map(([s, l]) => (
                <button key={s} disabled={wo.status === s}
                  style={{ padding:'9px 12px', border:'1px solid var(--border-strong)', borderRadius:'var(--radius-sm)', background: wo.status===s ? 'var(--surface)' : '#fff', color:wo.status===s?'var(--ink-muted)':'var(--ink)', fontSize:13, cursor:wo.status===s?'default':'pointer', fontFamily:'var(--font)', textAlign:'left', opacity:wo.status===s?.6:1 }}
                  onClick={() => { setStatusForm({status:s,actual_cost:'',notes:''}); setModal(true); }}>
                  {s === wo.status ? '● ' : '○ '} {l}
                </button>
              ))}
              <div style={{ marginTop:8, paddingTop:8, borderTop:'1px solid var(--border)' }}>
                <Button variant="primary" style={{ width:'100%' }} onClick={() => navigate(`/assets/${wo.asset_id}`)}>View Asset →</Button>
              </div>
            </div>
          </CardBody>
        </Card>
      </div>

      {/* Status Update Modal */}
      <Modal open={modal} onClose={() => setModal(false)} title="Update Work Order Status" size="sm">
        <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
          <Select label="New Status" value={statusForm.status} onChange={e => setStatusForm(f=>({...f,status:e.target.value}))}>
            {[['open','Open'],['assigned','Assigned'],['in_progress','In Progress'],['on_hold','On Hold'],['closed','Closed'],['cancelled','Cancelled']].map(([v,l]) => <option key={v} value={v}>{l}</option>)}
          </Select>
          <Input label="Actual Cost ₹ (optional)" type="number" value={statusForm.actual_cost} onChange={e=>setStatusForm(f=>({...f,actual_cost:e.target.value}))} placeholder="0" />
          <Textarea label="Notes (optional)" value={statusForm.notes} onChange={e=>setStatusForm(f=>({...f,notes:e.target.value}))} rows={3} placeholder="Add a note…" />
          <div style={{ display:'flex', gap:10, paddingTop:8 }}>
            <Button variant="primary" onClick={updateStatus} loading={updating}>Save Status</Button>
            <Button onClick={() => setModal(false)}>Cancel</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
